import React, { useMemo, useState } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { observer } from "mobx-react-lite";
import { Ionicons } from "@expo/vector-icons";
import FloatingMenu from "../../Components/FloatingMenu";
import Tooltip from "../../Components/Tooltip";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { ProtocolTitles } from "../../../Resources/Constants";
import { useDevice } from "../../_layout";

const getSlotLabel = ({ t, track }) => {
  if (!track) return "";
  const mode = track.slotMode ?? "any";
  if (mode !== "exact") return t ? t("statisticsAllSlots-string") : "All slots";

  const key = track.protocolSlot;
  if (!key) return t ? t("statisticsAllSlots-string") : "All slots";

  try {
    return t ? t(key) : String(key);
  } catch (e) {
    return String(key);
  }
};

const DropdownTrigger = ({ color, isSelected }) => {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);

  const hoverBg = isSelected ? "rgba(255, 255, 255, 0.2)" : Colors.borderColor;

  return (
    <View
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: 26,
        width: 28,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isHovered ? hoverBg : "transparent",
      }}
    >
      <Ionicons name="chevron-down" size={14} color={color} />
    </View>
  );
};

const ExerciseTrackPill = observer(
  ({
    exerciseDisplayName,
    selected,
    slotMode,
    protocolSlot,
    availableProtocolSlots,
    t,
    onToggle,
    onChangeSlot,
  }) => {
    const { isMobile } = useDevice();
    const [isHovered, setIsHovered] = useState(false);

    const slotOptions = useMemo(() => {
      // If the caller provides an array (even empty), treat it as authoritative.
      if (Array.isArray(availableProtocolSlots)) {
        const normalized = availableProtocolSlots
          .map((item) => {
            if (typeof item === "string")
              return { value: item, disabled: false };
            if (item && typeof item === "object" && item.value) return item;
            return null;
          })
          .filter(Boolean);

        const seen = new Set();
        const deduped = [];
        normalized.forEach((item) => {
          if (!seen.has(item.value)) {
            seen.add(item.value);
            deduped.push(item);
          }
        });

        return deduped.sort((a, b) =>
          String(a.value).localeCompare(String(b.value)),
        );
      }

      // Fallback only when we truly don't know (e.g., legacy caller).
      const fallbackAll = Object.values(ProtocolTitles ?? {}).filter(Boolean);
      const uniq = Array.from(new Set(fallbackAll));
      return uniq
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((k) => ({ value: k, disabled: false }));
    }, [availableProtocolSlots]);

    const showSlotDropdown = (slotOptions?.length ?? 0) > 1;

    // If the caller explicitly provides an empty slots array, it means this exercise has
    // no protocolSlot at all in the current stats scope.
    const hasNoProtocolSlotsInScope =
      Array.isArray(availableProtocolSlots) && (slotOptions?.length ?? 0) === 0;

    const slotLabel = useMemo(() => {
      const translate = (key) => {
        try {
          return t ? t(key) : String(key);
        } catch (e) {
          return String(key);
        }
      };

      if (hasNoProtocolSlotsInScope) return "";

      const mode = slotMode ?? "any";

      // If the current selection is an exact slot, show it.
      if (mode === "exact" && protocolSlot) {
        return translate(protocolSlot);
      }

      // If there's only one real slot available for this exercise, show it instead of "All slots".
      if ((slotOptions?.length ?? 0) === 1) {
        return translate(slotOptions[0].value);
      }

      return t ? t("statisticsAllSlots-string") : "All slots";
    }, [slotMode, protocolSlot, slotOptions, t, hasNoProtocolSlotsInScope]);

    const items = useMemo(() => {
      return [
        {
          id: "any",
          slotMode: "any",
          protocolSlot: null,
          label: t ? t("statisticsAllSlots-string") : "All slots",
          disabled: false,
        },
        ...slotOptions.map((item) => ({
          id: item.value,
          slotMode: "exact",
          protocolSlot: item.value,
          label: t ? t(item.value) : item.value,
          disabled: item.disabled,
        })),
      ];
    }, [slotOptions, t]);

    const isSelected = !!selected;
    const pillBg = isSelected
      ? isHovered
        ? Colors.messageBubbleColor
        : Colors.bubbleColor
      : isHovered
        ? Colors.borderColor
        : Colors.lightGrayColor;

    const titleColor = isSelected ? Colors.whiteColor : Colors.darkgrayColor;
    const subtitleColor = isSelected ? Colors.whiteColor : Colors.darkgrayColor;
    // Align dropdown vertically in the center. The container has alignItems: center so this is already true.
    // justifyContent in container is not set but gap handles spacing.

    const showDropdownNow = isSelected && showSlotDropdown;

    return (
      <View
        style={{ flexDirection: "row", alignItems: "center" }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TouchableOpacity
          onPress={() => onToggle?.()}
          activeOpacity={0.75}
          style={{
            paddingLeft: 10,
            paddingRight: showDropdownNow ? 6 : 10,
            height: 30,
            borderRadius: 12,
            borderWidth: 0,
            backgroundColor: pillBg,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: showDropdownNow ? "flex-start" : "center",
            gap: 8,
            maxWidth: 300, // keep max width
            opacity: isSelected ? 1 : 0.95,
          }}
        >
          <View
            style={{
              flexShrink: 1,
              minWidth: 0,
              alignItems: showDropdownNow ? "flex-start" : "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                color: titleColor,
                fontSize: 12,
                fontWeight: "600",
                textAlign: showDropdownNow ? "left" : "center",
              }}
              numberOfLines={1}
            >
              {exerciseDisplayName}
            </Text>
            {!hasNoProtocolSlotsInScope && (
              <Text
                style={[
                  Fonts.regular10,
                  {
                    color: subtitleColor,
                    opacity: isSelected ? 0.85 : 0.75,
                    marginTop: -1,
                    textAlign: showDropdownNow ? "left" : "center",
                  },
                ]}
                numberOfLines={1}
              >
                {slotLabel}
              </Text>
            )}
          </View>

          {showDropdownNow && (
            <>
              <View style={{ flex: 1 }} />
              <FloatingMenu
                placement="bottom-start"
                width={260}
                maxHeight={300}
                items={items}
                trigger={
                  <DropdownTrigger color={titleColor} isSelected={isSelected} />
                }
                renderItem={({ item, close }) => (
                  <Tooltip
                    disabled={!item.disabled}
                    content={
                      t
                        ? t("statisticsProtocolSlotNotSharedTooltip-string")
                        : "statisticsProtocolSlotNotSharedTooltip-string"
                    }
                    placement="right"
                    offsetDistance={10}
                  >
                    <TouchableOpacity
                      // Keep hover/click events available for Tooltip.
                      // Prevent selecting a disabled item by guarding in onPress.
                      onPress={() => {
                        if (item.disabled) return;
                        close?.();
                        onChangeSlot?.({
                          slotMode: item.slotMode,
                          protocolSlot: item.protocolSlot,
                        });
                      }}
                      activeOpacity={item.disabled ? 1 : 0.75}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 12,
                        width: "100%",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        opacity: item.disabled ? 0.4 : 1,
                      }}
                    >
                      <Text
                        style={[
                          Fonts.regular14,
                          {
                            color: Colors.blackColor,
                            flexShrink: 1,
                            fontSize: 13,
                          },
                        ]}
                        numberOfLines={1}
                      >
                        {item.label}
                      </Text>
                      {(slotMode ?? "any") === item.slotMode &&
                        (item.slotMode === "any"
                          ? (protocolSlot ?? null) === null
                          : (protocolSlot ?? null) === item.protocolSlot) && (
                          <Ionicons
                            name="checkmark"
                            size={16}
                            color={Colors.bubbleColor}
                          />
                        )}
                    </TouchableOpacity>
                  </Tooltip>
                )}
              />
            </>
          )}
        </TouchableOpacity>
      </View>
    );
  },
);

export default ExerciseTrackPill;
